import { ChildBillAdditionalComponent } from 'types';
import styled from 'styled-components';
import { AppText } from 'components/atoms';
import { EMPTY_VALUE_LABEL } from 'consts/labels/common';
import { settlementVatRateTypeLabels } from 'consts/settlementVatRates/settlementVatRateLabels';
import { getFormattedPrice } from 'utils/getFormattedPrice';
import { If } from '../../../../../../../atoms/If/If';

type ChildBillAdditionalComponentRowProps = Pick<
    ChildBillAdditionalComponent,
    | 'name'
    | 'amountString'
    | 'discountString'
    | 'vatRate'
    | 'vatAmount'
    | 'netAmount'
    | 'grossAmount'
    | 'units'
    | 'unitGrossPrice'
> & {
    isPrepaid: boolean;
};

const TableRow = styled.div`
    display: flex;
    align-items: center;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    padding: 12px 0;

    &:hover {
        background: #f1f5f9;
    }

    @media (max-width: 768px) {
        padding: 8px 0;
    }
`;

const TableCell = styled.div<{ $width?: string; $align?: 'left' | 'center' | 'right' }>`
    flex: ${({ $width }) => ($width ? `0 0 ${$width}` : '1')};
    padding: 0 8px;
    text-align: ${({ $align }) => $align || 'left'};

    &:first-child {
        padding-left: 40px;
    }

    @media (max-width: 768px) {
        padding: 0 4px;

        &:first-child {
            padding-left: 20px;
        }
    }
`;

export const ChildBillAdditionalComponentRow = ({
    isPrepaid,
    name,
    discountString,
    vatRate,
    vatAmount,
    netAmount,
    grossAmount,
    units,
    unitGrossPrice,
}: ChildBillAdditionalComponentRowProps) => {
    const columnWidth = isPrepaid ? '15%' : '20%';
    const cellLeftMargin = 5;

    return (
        <TableRow>
            {/* Expandable action column */}
            <TableCell $width="10%" />
            {/* Type/Name column */}
            <TableCell $width="15%">
                <AppText
                    margin={0}
                    marginLeft={cellLeftMargin}
                    style={{
                        color: '#1e293b',
                        fontWeight: 600,
                        fontSize: 16,
                    }}
                >
                    {name}
                </AppText>
            </TableCell>

            <TableCell $width={columnWidth}>
                <AppText
                    margin={0}
                    marginLeft={cellLeftMargin}
                    style={{
                        fontWeight: 500,
                        fontSize: 15,
                    }}
                >
                    {units
                        ? `${units} x ${getFormattedPrice(unitGrossPrice)}`
                        : getFormattedPrice(grossAmount)}
                </AppText>
            </TableCell>

            <If condition={isPrepaid}>
                <TableCell $width={columnWidth}>
                    <AppText
                        margin={0}
                        marginLeft={cellLeftMargin}
                        style={{
                            fontWeight: 500,
                            fontSize: 15,
                        }}
                    >
                        {'-'}
                    </AppText>
                </TableCell>
            </If>

            <TableCell $width={columnWidth}>
                <AppText
                    margin={0}
                    marginLeft={cellLeftMargin}
                    style={{
                        color: '#64748b',
                    }}
                >
                    {getFormattedPrice(netAmount)}
                </AppText>
            </TableCell>

            <TableCell $width={columnWidth}>
                <AppText
                    margin={0}
                    marginLeft={cellLeftMargin}
                    style={{
                        color: '#64748b',
                    }}
                >
                    {getFormattedPrice(vatAmount)}{' '}
                    {vatRate ? `(${settlementVatRateTypeLabels[vatRate]})` : ''}
                </AppText>
            </TableCell>

            <TableCell $width="15%">
                <AppText
                    margin={0}
                    marginLeft={cellLeftMargin}
                    style={{
                        color: discountString ? '#be185d' : '#64748b',
                        fontStyle: discountString ? 'normal' : 'italic',
                        fontWeight: 400,
                        fontSize: 15,
                    }}
                >
                    {discountString || EMPTY_VALUE_LABEL}
                </AppText>
            </TableCell>
        </TableRow>
    );
};
